import { isPointInPolygon } from "../isPointInPolygon";
import type { GroupedSmartSurveyorElement } from "components/SmartSurveyor/ReviewPage/types";
import type { PlanSelectionArea, SelectionAreaPoint } from "../../types";
import type { PageAreaGroup } from "../types";
import type { FilterExportElementsBySelectionAreas } from "./types";

const isInsideAnyArea = (
  point: SelectionAreaPoint,
  areas: PlanSelectionArea[],
): boolean =>
  areas.some((area) => isPointInPolygon({ point, ring: area.points }));

// Mirrors the backend rule: excludes drop what they catch; any include area on a
// page flips it to keep only what include areas catch; exclude wins on overlap.
const shouldDropElement = (
  bboxCenter: SelectionAreaPoint,
  pageGroup: PageAreaGroup,
): boolean => {
  if (isInsideAnyArea(bboxCenter, pageGroup.excludeAreas)) return true;
  if (pageGroup.includeAreas.length === 0) return false;
  return !isInsideAnyArea(bboxCenter, pageGroup.includeAreas);
};

export const filterExportElementsBySelectionAreas: FilterExportElementsBySelectionAreas =
  ({ byGroupId, selectionAreas }) => {
    const areasByPageIndex = new Map<number, PageAreaGroup>();
    for (const area of selectionAreas) {
      if (!area.appliesToElements || area.points.length < 3) continue;
      const pageGroup = areasByPageIndex.get(area.pageIndex) ?? {
        includeAreas: [],
        excludeAreas: [],
      };
      if (area.mode === "include") {
        pageGroup.includeAreas.push(area);
      } else {
        pageGroup.excludeAreas.push(area);
      }
      areasByPageIndex.set(area.pageIndex, pageGroup);
    }
    if (areasByPageIndex.size === 0) return byGroupId;

    const filtered: Record<string, GroupedSmartSurveyorElement[]> = {};
    for (const [groupId, elements] of Object.entries(byGroupId)) {
      filtered[groupId] = elements.filter((element) => {
        const pageGroup = areasByPageIndex.get(element.page ?? 0);
        if (!pageGroup) return true;

        const bboxCenter = {
          x: (element.bbox.x1 + element.bbox.x2) / 2,
          y: (element.bbox.y1 + element.bbox.y2) / 2,
        };
        return !shouldDropElement(bboxCenter, pageGroup);
      });
    }
    return filtered;
  };
